'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { ChevronDown, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MobileAccountSettingsNav, MOBILE_ACCOUNT_SETTINGS_GROUP_KEY } from '@/components/layout/mobile-account-settings-nav';
import { MobileLanguageQuickButton } from '@/components/layout/mobile-language-quick-button';
import {
  mobileAdminRowClass,
  mobileDropdownChildClass,
  mobileDropdownChildListClass,
  mobileDropdownParentClass,
  mobileNavIconClass,
  mobileNavRowClass,
  mobileSectionDividerClass,
  MOBILE_DROPDOWN_CHILD_ROW,
  MOBILE_NAV_ICON_SLOT,
  type MobileSidePanelTheme,
} from '@/components/layout/mobile-app-side-panel-theme';
import { NavbarLocalhostBadge } from '@/components/layout/navbar-localhost-badge';
import { NavItemIcon } from '@/components/layout/nav-item-icon';
import { Badge } from '@/components/ui/badge';
import { SheetClose } from '@/components/ui/sheet';
import { useAppNavItems } from '@/hooks/use-app-nav-items';
import { useAppState } from '@/hooks/use-app-state';
import { isNavActive, isNavDropdownActive } from '@/lib/navigation/is-nav-active';
import type { AppNavItem } from '@/lib/navigation/app-nav-types';
import {
  isMarketingDesktopOnlyPath,
  MARKETING_MOBILE_ENTRY_PATH,
} from '@/lib/navigation/marketing-desktop-only-paths';
import {
  hasPaidSubscriptionClient,
  isSuperAdminViewClient,
} from '@/lib/permissions/client';
import { useRTLUtils } from '@/lib/translation';
import { cn } from '@/lib/utils';

function orderMobileNavItems(items: AppNavItem[]): AppNavItem[] {
  const reversed = [...items].reverse();
  const desktopOnly = reversed.filter((item) => item.mobileDesktopOnly);
  const rest = reversed.filter((item) => !item.mobileDesktopOnly);
  return [...rest, ...desktopOnly];
}

type PreviewNavLink = {
  href: string;
  label: string;
  icon: string;
  isActive: (pathname: string) => boolean;
};

export type MobileAppSidePanelProps = {
  theme: MobileSidePanelTheme;
  onNavigate?: () => void;
  showLocalBadge?: boolean;
};

export function MobileAppSidePanel({
  theme,
  onNavigate,
  showLocalBadge = false,
}: MobileAppSidePanelProps) {
  const pathname = usePathname() ?? '';
  const { t } = useTranslation();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const { orgRole, sessionClaims } = useAuth();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const hasPaid = hasPaidSubscriptionClient(sessionClaims);
  const { navItems, isSubscriptionView, usageTabColor } = useAppNavItems();
  const { data: appState } = useAppState();
  const pendingFeedbackApprovals = appState?.pendingFeedbackApprovals ?? 0;
  const showVotesPendingBadge = pendingFeedbackApprovals > 0;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isActive = (href: string) => isNavActive(pathname, href);
  const isDropdownActive = (dropdownItems?: Array<{ href: string; label: string }>) =>
    isNavDropdownActive(pathname, dropdownItems);

  const displayItems = orderMobileNavItems(navItems);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of navItems) {
        if (item.mobileDesktopOnly || !item.isDropdown || !item.dropdownItems) continue;
        if (isNavDropdownActive(pathname, item.dropdownItems) && !next[item.label]) {
          next[item.label] = true;
          changed = true;
        }
      }
      if (pathname.startsWith('/settings') && !next[MOBILE_ACCOUNT_SETTINGS_GROUP_KEY]) {
        next[MOBILE_ACCOUNT_SETTINGS_GROUP_KEY] = true;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [pathname, navItems]);

  const partnersHref = hasPaid ? '/partners' : '/partners?preview=1';

  const superAdminLinks: PreviewNavLink[] = isSuperAdmin
    ? [
        {
          href: '/reachstore',
          label: t('navigation.store'),
          icon: 'store',
          isActive: (p) => isNavActive(p, '/reachstore'),
        },
        {
          href: partnersHref,
          label: t('navigation.partners'),
          icon: 'partners',
          isActive: (p) => p === '/partners' || p.startsWith('/partners/'),
        },
        {
          href: '/admin',
          label: 'Admin',
          icon: 'admin',
          isActive: (p) => p === '/admin',
        },
        {
          href: '/admin/outreach/go',
          label: 'Outreach',
          icon: 'outreach',
          isActive: (p) => p === '/admin/outreach' || p.startsWith('/admin/outreach/'),
        },
        {
          href: '/admin/marketing-os/go',
          label: 'Marketing',
          icon: 'marketingOs',
          isActive: (p) => p === '/admin/marketing-os' || p.startsWith('/admin/marketing-os/'),
        },
        {
          href: '/admin/org-management',
          label: 'Organizations',
          icon: 'orgManagement',
          isActive: (p) =>
            p === '/admin/org-management' || p.startsWith('/admin/org-management/'),
        },
      ]
    : [];

  const renderSuperAdminLink = (item: PreviewNavLink) => {
    const active = item.isActive(pathname);
    return (
      <SheetClose asChild key={item.href}>
        <Link
          href={item.href}
          onClick={onNavigate}
          className={cn(mobileAdminRowClass(theme, active), textAlignClass)}
        >
          <span className={MOBILE_NAV_ICON_SLOT}>
            <NavItemIcon
              icon={item.icon}
              className={theme === 'enterprise' ? (active ? 'text-amber-50' : 'text-amber-200/90') : undefined}
            />
          </span>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </Link>
      </SheetClose>
    );
  };

  const renderNavItem = (item: AppNavItem) => {
    if (item.mobileDesktopOnly) {
      const isMarketingActive = isMarketingDesktopOnlyPath(pathname);
      return (
        <SheetClose asChild key={item.label}>
          <Link
            href={MARKETING_MOBILE_ENTRY_PATH}
            onClick={onNavigate}
            className={cn(mobileNavRowClass(theme, isMarketingActive), textAlignClass)}
          >
            <span className={MOBILE_NAV_ICON_SLOT}>
              <NavItemIcon
                icon={item.icon}
                className={mobileNavIconClass(theme, isMarketingActive)}
              />
            </span>
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </Link>
        </SheetClose>
      );
    }

    if (item.isDropdown && item.dropdownItems) {
      const groupHasActiveChild = isDropdownActive(item.dropdownItems);
      const parentTriggerActive =
        item.href !== '#' && item.href !== '' && isActive(item.href);
      const isOpen = openGroups[item.label] ?? groupHasActiveChild;

      return (
        <div key={item.label} className="flex w-full flex-col">
          <button
            type="button"
            onClick={() =>
              setOpenGroups((prev) => ({
                ...prev,
                [item.label]: !(prev[item.label] ?? groupHasActiveChild),
              }))
            }
            className={mobileDropdownParentClass(theme, groupHasActiveChild || parentTriggerActive)}
            aria-expanded={isOpen}
          >
            <span className={MOBILE_NAV_ICON_SLOT}>
              <NavItemIcon
                icon={item.icon}
                className={mobileNavIconClass(theme, groupHasActiveChild || parentTriggerActive)}
              />
            </span>
            <span className={cn('min-w-0 flex-1 truncate', textAlignClass)}>{item.label}</span>
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 opacity-70 transition-transform', isOpen && 'rotate-180')}
            />
          </button>
          {isOpen ? (
            <div className={mobileDropdownChildListClass(theme, isRTL)}>
              {item.dropdownItems.map((child) => {
                const childActive = isActive(child.href);
                const isVotesChild = child.href === '/votes' || child.href.startsWith('/votes?');
                return (
                  <SheetClose asChild key={child.href}>
                    <Link
                      href={child.href}
                      onClick={onNavigate}
                      className={cn(
                        MOBILE_DROPDOWN_CHILD_ROW,
                        mobileDropdownChildClass(theme, childActive),
                        textAlignClass,
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{child.label}</span>
                      {isVotesChild && showVotesPendingBadge ? (
                        <Badge
                          variant="outline"
                          className="h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                        >
                          {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                        </Badge>
                      ) : null}
                    </Link>
                  </SheetClose>
                );
              })}
            </div>
          ) : null}
        </div>
      );
    }

    const isBillingTab = item.href === '/billing';
    const isActiveTab = isActive(item.href);
    const isVotesItem = item.href === '/votes' || item.href.startsWith('/votes?');

    return (
      <SheetClose asChild key={item.href}>
        <Link
          href={item.href}
          onClick={onNavigate}
          className={cn(
            mobileNavRowClass(theme, isActiveTab, {
              isBilling: isBillingTab,
              isSubscriptionView,
              hasPaid,
              usageTabColor,
              isSuperAdminTab: Boolean(item.superAdminOnly),
            }),
            'relative',
            textAlignClass,
          )}
        >
          <span className={MOBILE_NAV_ICON_SLOT}>
            {item.icon === 'achievements' ? (
              <Trophy className="h-4 w-4 shrink-0 text-amber-500" />
            ) : (
              <NavItemIcon
                icon={item.icon}
                className={
                  item.superAdminOnly
                    ? theme === 'enterprise'
                      ? isActiveTab
                        ? 'text-amber-50'
                        : 'text-amber-200/90'
                      : undefined
                    : mobileNavIconClass(theme, isActiveTab)
                }
              />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {isVotesItem && showVotesPendingBadge ? (
            <Badge
              variant="outline"
              className="h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
            >
              {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
            </Badge>
          ) : null}
        </Link>
      </SheetClose>
    );
  };

  const accountSettingsOpen =
    openGroups[MOBILE_ACCOUNT_SETTINGS_GROUP_KEY] ?? pathname.startsWith('/settings');

  const showSidePanelLanguage = !isSuperAdmin;
  const showSidePanelFooter = showSidePanelLanguage || showLocalBadge;

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden px-1.5 py-2',
        theme === 'enterprise' ? 'text-emerald-50' : '',
        directionClass,
      )}
    >
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col gap-0.5">
          <SheetClose asChild>
            <Link
              href="/dashboard"
              onClick={onNavigate}
              className={cn(mobileNavRowClass(theme, isActive('/dashboard')), textAlignClass)}
            >
              <span className={MOBILE_NAV_ICON_SLOT}>
                <NavItemIcon
                  icon="dashboard"
                  className={mobileNavIconClass(theme, isActive('/dashboard'))}
                />
              </span>
              <span className="min-w-0 flex-1 truncate">{t('navigation.dashboard')}</span>
            </Link>
          </SheetClose>

          <nav className="flex w-full flex-col gap-px" aria-label="Main">
            {displayItems.map((item) => renderNavItem(item))}
            <MobileAccountSettingsNav
              theme={theme}
              onNavigate={onNavigate}
              isOpen={accountSettingsOpen}
              onToggle={() =>
                setOpenGroups((prev) => ({
                  ...prev,
                  [MOBILE_ACCOUNT_SETTINGS_GROUP_KEY]: !accountSettingsOpen,
                }))
              }
            />
          </nav>

          {superAdminLinks.length > 0 ? (
            <>
              <div className={mobileSectionDividerClass(theme)} aria-hidden />
              <div className="flex w-full flex-col gap-px">
                {superAdminLinks.map((item) => renderSuperAdminLink(item))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {showSidePanelFooter ? (
        <div className={cn('mt-2 flex shrink-0 flex-col gap-2 pt-2', directionClass)}>
          {showSidePanelLanguage ? (
            <div className="flex items-center gap-2 px-1">
              <MobileLanguageQuickButton theme={theme} />
            </div>
          ) : null}
          {showLocalBadge ? (
            <div className="flex shrink-0 items-center px-1">
              <NavbarLocalhostBadge className="text-[2rem] leading-none sm:text-[2.125rem]" />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
